import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Card, Form, Input, Skeleton, message } from "antd";

import { type SalesCallConfigData } from "@fitness-app/data-models/entities/SalesCallConfig";
import { AppConfigType, type SalesCallAppConfig } from "@fitness-app/data-models/entities/TrainerConfig";
import { type Json } from "@fitness-app/supabase";

import { supabase } from "~/store/initializeStore";

// Wydarzenie w Calendly dla stałego linku /rozmowa (TRE-2465) - app_config typu sales_call.
// Zmiana eventu (np. na nowy rok) nie wymaga zmiany linku w mailach i SMS-ach z ActiveCampaign.

const queryKey = ["app_config", AppConfigType.SALES_CALL];

const CALENDLY_EVENT_TYPE_PATTERN = /^https:\/\/(api\.calendly\.com\/event_types\/|calendly\.com\/)[^\s]+$/;

export const SalesCallCard = (): React.ReactElement => {
  const queryClient = useQueryClient();
  const [form] = Form.useForm<SalesCallConfigData>();

  const configQuery = useQuery({
    queryKey,
    retry: false,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("app_config")
        .select()
        .eq("type", AppConfigType.SALES_CALL)
        .maybeSingle<SalesCallAppConfig>();

      if (error) throw error;

      return data;
    },
  });

  useEffect(() => {
    if (configQuery.data?.data) {
      form.setFieldsValue(configQuery.data.data);
    }
  }, [configQuery.data, form]);

  const updateConfigMutation = useMutation({
    mutationFn: async (values: SalesCallConfigData) => {
      const config = configQuery.data;
      if (!config?.id) {
        throw new Error("Brak rekordu konfiguracji rozmowy sprzedażowej (app_config, typ sales_call).");
      }

      const { data, error } = await supabase
        .from("app_config")
        .update({
          data: { ...config.data, calendlyEventType: values.calendlyEventType ?? "" } as unknown as Json,
          updatedAt: new Date().toISOString(),
          version: (config.version ?? 0) + 1,
        })
        .eq("id", config.id)
        .eq("version", config.version ?? 0)
        .select()
        .maybeSingle<SalesCallAppConfig>();

      if (error) throw error;
      if (!data) {
        await queryClient.invalidateQueries({ queryKey });
        // Zero rows means either a concurrent save (version moved) or RLS refusing the
        // update, and the two are indistinguishable from here.
        throw new Error(
          "Zapis nie objął żadnego wiersza: konfiguracja zmieniła się w innym miejscu albo Twoje konto nie ma prawa jej edytować.",
        );
      }

      return data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(queryKey, data);
      void message.success("Wydarzenie dla /rozmowa zapisane.");
    },
    onError: (error) => {
      void message.error(error instanceof Error ? error.message : "Nie udało się zapisać konfiguracji.");
    },
  });

  return (
    <Card size="small" title="Rozmowa sprzedażowa (stały link /rozmowa)" style={{ marginBottom: 16 }}>
      {configQuery.isLoading ? (
        <Skeleton active paragraph={{ rows: 1 }} />
      ) : configQuery.isError || !configQuery.data ? (
        <Alert
          type="warning"
          showIcon
          message="Brak konfiguracji rozmowy sprzedażowej"
          description="Nie znaleziono rekordu app_config typu sales_call. /rozmowa umawia do domyślnego eventu VIP."
        />
      ) : (
        <Form form={form} layout="vertical" onFinish={(values) => updateConfigMutation.mutate(values)}>
          <Form.Item
            name="calendlyEventType"
            label="Wydarzenie w Calendly"
            tooltip="Link do umawiania wydarzenia z Calendly. Puste pole oznacza „Metamorfoza Hollywood - 2026 - VIP”. Inne wydarzenie musi należeć do zespołu sprzedaży i mieć te same pytania w tej samej kolejności co VIP. Jeśli nie spełnia tych warunków, /rozmowa zostaje przy domyślnym."
            rules={[
              {
                pattern: CALENDLY_EVENT_TYPE_PATTERN,
                message: "Wklej link z Calendly, np. https://calendly.com/sylwetkahollywood/nazwa-wydarzenia",
              },
            ]}
            normalize={(value: string) => value?.trim()}
          >
            <Input
              allowClear
              placeholder="https://calendly.com/sylwetkahollywood/metamorfoza-hollywood-2-2026"
              style={{ maxWidth: 420 }}
            />
          </Form.Item>

          <Button type="primary" htmlType="submit" loading={updateConfigMutation.isLoading}>
            Zapisz
          </Button>
        </Form>
      )}
    </Card>
  );
};
